import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { EmailTemplateDto } from "@babackapp/private-api-client/generated/schema";
import useGetAllShopEmailTemplatesSuspense from "@babackapp/private-api-client/client/use-get-all-shop-email-templates";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@babackapp/ui/components/alert-dialog";
import { Button } from "@babackapp/ui/components/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@babackapp/ui/components/dropdown-menu";
import {
  FileItem,
  FileItemAction,
  FileItemActions,
  FileItemDetails,
  FileItemInfos,
  FileItemTitle,
  FileItemTypeIcon,
  FileList,
} from "@babackapp/ui/components/files";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "@babackapp/ui/components/input-group";
import { cn } from "@babackapp/ui/lib/utils";
import { Copy, EarthIcon, Edit, Mail, MoreVertical, PlusIcon, RefreshCw, SearchIcon, TrashIcon } from "lucide-react";
import useResetEmailTemplate from "@babackapp/private-api-client/client/use-reset-email-template";
import useCurrentShopSuspense from "./hooks/use-current-shop-suspense";
import { useTemplatesEditorConfig } from "./context/templates-editor-config-context";
import { useTemplatesEditorRouting } from "./context/templates-editor-routing-context";
import { useCreateTemplate } from "./hooks/use-create-template";
import { useDeleteTemplate } from "./hooks/use-delete-template";
import { useDuplicateTemplate } from "./hooks/use-duplicate-template";

export type ReturnTemplatesListProps = {
  onTemplateHover?: (template: EmailTemplateDto | null) => void;
  className?: string;
};

/**
 * Dedicated list + blank-create flow for return email templates (baback-office).
 * Scoped to `domain: "return"` via the return config; mirrors the tracking list
 * but without tracking-only actions (default template, generated templates).
 *
 * Assumes a `ReturnTemplatesProvider` above it (which supplies shopId +
 * navigation + return config) — wrap the list page once so a side-by-side
 * preview shares the same context.
 */
export function ReturnTemplatesList({
  onTemplateHover,
  className,
}: ReturnTemplatesListProps) {
  const { t } = useTranslation("trackoffice");
  const { data: shop } = useCurrentShopSuspense();
  const { domain } = useTemplatesEditorConfig();
  const { openTemplate } = useTemplatesEditorRouting();
  const { data: templates = [] } = useGetAllShopEmailTemplatesSuspense(
    shop.id,
    domain,
  );
  const duplicateTemplate = useDuplicateTemplate();
  const { createTemplate, isLoading: isCreating } = useCreateTemplate();

  const [search, setSearch] = useState("");

  const filtered = useMemo(
    () =>
      templates.filter((template) =>
        template.title.toLowerCase().includes(search.toLowerCase()),
      ),
    [templates, search],
  );

  const handleCreate = async () => {
    const template = await createTemplate({ type: "email" });
    if (template?.id) {
      openTemplate?.(template.id);
    }
  };

  return (
    <div className={cn("flex relative flex-col h-full", className)}>
      <div className="z-50 flex-shrink-0">
        <InputGroup>
          <InputGroupInput
            placeholder={t(
              "returnTemplatesList.searchPlaceholder",
              "Search a template...",
            )}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <InputGroupAddon>
            <SearchIcon />
          </InputGroupAddon>
        </InputGroup>
      </div>

      <div className="z-0 pt-4 flex-1 overflow-auto">
        <FileList>
          {filtered.map((template) => (
            <ReturnTemplateRow
              key={template.id}
              template={template}
              onDuplicate={() => duplicateTemplate(template.id)}
              onHover={onTemplateHover}
            />
          ))}
        </FileList>
      </div>

      <div className="flex justify-end pt-4 border-t">
        <Button onClick={handleCreate} disabled={isCreating}>
          <PlusIcon />
          {t("returnTemplatesList.addNewTemplate", "New template")}
        </Button>
      </div>
    </div>
  );
}

function ReturnTemplateRow({
  template,
  onDuplicate,
  onHover,
}: {
  template: EmailTemplateDto;
  onDuplicate: () => void;
  onHover?: (template: EmailTemplateDto | null) => void;
}) {
  const { t } = useTranslation("trackoffice");
  const { openTemplate } = useTemplatesEditorRouting();
  const {
    isDeleteDialogOpen,
    setIsDeleteDialogOpen,
    handleDeleteClick,
    openDeleteDialog,
    hasLinkedNotifications,
    isLinkedToReturnConfig,
    dependantWorkflows,
    isProtected,
  } = useDeleteTemplate(template.id, template.isProtected ?? false);

  const isDeleteDisabled = isProtected || hasLinkedNotifications;

  const { data: shop } = useCurrentShopSuspense();
  const resetTemplate = useResetEmailTemplate(shop.id);
  const [isResetDialogOpen, setIsResetDialogOpen] = useState(false);
  // A template can be reset only when it mirrors a packaged default.
  const canReset = Boolean(template.emailDefaultTemplateType);

  const handleResetConfirm = () => {
    resetTemplate.mutate(template.id);
    setIsResetDialogOpen(false);
  };

  return (
    <>
      <FileItem
        onMouseEnter={() => onHover?.(template)}
        onMouseLeave={() => onHover?.(null)}
      >
        <FileItemTypeIcon>
          {template.type === "web" ? <EarthIcon /> : <Mail />}
        </FileItemTypeIcon>
        <a
          className="flex-1 cursor-pointer"
          onClick={() => openTemplate?.(template.id)}
        >
          <FileItemInfos>
            <FileItemTitle>{template.title}</FileItemTitle>
            <FileItemDetails>
              {t("returnTemplatesList.modifiedOn", "Modified on")}{" "}
              {new Date(template.updatedAt).toLocaleDateString()}
            </FileItemDetails>
          </FileItemInfos>
        </a>
        <FileItemActions>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <FileItemAction tooltip="Actions">
                <MoreVertical />
              </FileItemAction>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-[200px]">
              <DropdownMenuItem onClick={() => openTemplate?.(template.id)}>
                <Edit />
                {t("returnTemplatesList.editTemplate", "Edit")}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onDuplicate}>
                <Copy />
                {t("returnTemplatesList.duplicateTemplate", "Duplicate")}
              </DropdownMenuItem>
              {canReset && (
                <DropdownMenuItem
                  disabled={resetTemplate.isPending}
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsResetDialogOpen(true);
                  }}
                >
                  <RefreshCw />
                  {t(
                    "returnTemplatesList.resetTemplate",
                    "Reset to default",
                  )}
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                disabled={isDeleteDisabled}
                className={cn(
                  !isDeleteDisabled &&
                    "text-destructive focus:text-destructive",
                  isDeleteDisabled && "opacity-50 cursor-not-allowed",
                )}
                onClick={(e) => {
                  e.stopPropagation();
                  openDeleteDialog();
                }}
              >
                <div className="flex flex-col gap-2">
                  <span className="inline-flex gap-1 items-center">
                    <TrashIcon className="size-4" />
                    {t("returnTemplatesList.deleteTemplate", "Delete")}
                  </span>
                  {isProtected && (
                    <p className="text-xs text-muted-foreground">
                      {t(
                        "returnTemplatesList.cannotDeleteProtectedTemplate",
                        "This default template cannot be deleted.",
                      )}
                    </p>
                  )}
                  {!isProtected && isLinkedToReturnConfig && (
                    <p className="text-xs text-muted-foreground">
                      {t(
                        "returnTemplatesList.cannotDeleteLinkedTemplate",
                        "This template is still used by a return notification. Please unlink it first.",
                      )}
                    </p>
                  )}
                  {!isProtected &&
                    !isLinkedToReturnConfig &&
                    dependantWorkflows.length > 0 && (
                      <p className="text-xs text-muted-foreground">
                        {t(
                          "returnTemplatesList.cannotDeleteWorkflowTemplate",
                          "This template is used by a return workflow ({names}). Remove it from the workflow first.",
                          {
                            names: dependantWorkflows
                              .map((workflow) => workflow.name)
                              .join(", "),
                          },
                        )}
                      </p>
                    )}
                </div>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </FileItemActions>
      </FileItem>

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t(
                "returnTemplatesList.deleteDialog.title",
                "Delete this template?",
              )}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                "returnTemplatesList.deleteDialog.description",
                "This action cannot be undone.",
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t("returnTemplatesList.deleteDialog.cancel", "Cancel")}
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteClick}>
              {t("returnTemplatesList.deleteDialog.confirm", "Delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={isResetDialogOpen} onOpenChange={setIsResetDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t(
                "returnTemplatesList.resetDialog.title",
                "Reset to default template?",
              )}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                "returnTemplatesList.resetDialog.description",
                "This restores the content, subject and preview to the default. Your changes will be lost. The title is kept.",
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t("returnTemplatesList.resetDialog.cancel", "Cancel")}
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleResetConfirm}>
              {t("returnTemplatesList.resetDialog.confirm", "Reset")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
